/**
 * Loan-purpose identity helpers.
 *
 * `loanPurposeId` is a `reference_data` FK, not a positional enum. Purchase is
 * seeded as 176 (177 refinance, 178 renewal); id 1 belongs to a completely
 * different category (province · Alberta). Code that compared
 * `loanPurposeId === 1` therefore treated every modern Purchase deal as a
 * Refinance, which rendered the refinance field set — where Sales Price is
 * `disabled: true` — so the price stayed 0 and FundMore rejected the submit.
 *
 * Resolve from reference data whenever the caller has the dropdown list; fall
 * back to the seeded id otherwise. Rows created before the reference-data
 * migration still carry 1, so that stays accepted.
 */

const PURCHASE_CODE = 'purchase';

// Seeded reference_data id — verified identical across dev/sbx and UAT.
// Only used when the caller has no reference list to resolve against.
const SEEDED_PURCHASE_ID = 176;

// Pre-reference-data applications still store 1.
const LEGACY_PURCHASE_ID = 1;

/** Unwrap `{ id }` objects — some forms hold the dropdown option, not the id. */
const toId = (value) => {
  const raw = value && typeof value === 'object' ? value.id : value;
  if (raw === null || raw === undefined || raw === '') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
};

/** Purchase's id from a reference_data list, or null when it isn't there. */
export const resolvePurchasePurposeId = (loanPurposeOptions) => {
  const match = (loanPurposeOptions || []).find(
    (option) =>
      String(option?.code ?? option?.name ?? '').toLowerCase() === PURCHASE_CODE
  );
  return match?.id ?? null;
};

/**
 * True when the loan purpose is Purchase.
 *
 * @param {number|string|{id:number}} loanPurposeId
 * @param {Array<{id:number, code?:string, name?:string}>} [loanPurposeOptions]
 *   reference_data rows; pass them when available so the check follows the
 *   environment rather than a constant.
 */
export const isPurchasePurpose = (loanPurposeId, loanPurposeOptions) => {
  const id = toId(loanPurposeId);
  if (id === null) return false;

  const resolved = toId(resolvePurchasePurposeId(loanPurposeOptions));
  if (resolved !== null) return id === resolved || id === LEGACY_PURCHASE_ID;

  return id === SEEDED_PURCHASE_ID || id === LEGACY_PURCHASE_ID;
};

export default isPurchasePurpose;
